import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { ApiProvider, AuthProvider, UserInfoProvider, useUserInfo } from "@mb-pro-ui/utils";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { useEffect, type PropsWithChildren } from "react";
import { Redirect, Route, Router, Switch } from "wouter";
import { homeOrgName, useMe } from "./api/useMe";
import { FullPageSpinner } from "./components/States";
import { API_BASE_URL } from "./config";
import LocaleProvider, { useLocale } from "./locales/LocaleProvider";
import OrgScope from "./org/OrgScope";
import { orgNameToSegment } from "./org/orgRouting";
import CustomerHub from "./pages/CustomerHub";
import CustomerSelector from "./pages/CustomerSelector";
import { NotifiablesPage, PartitionsPage, UsersPage, ZonesPage } from "./pages/Datasheet";
import EventsPage from "./pages/EventsPage";
import Login from "./pages/Login";
import { theme } from "./theme";

function Providers({ children }: PropsWithChildren) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UserInfoProvider>
        {/* The app's one socket connection; OrgScope moves it between the
            alarm rooms of the orgs. */}
        <ApiProvider baseUrl={API_BASE_URL}>
          <AuthProvider>
            <LocaleProvider>
              <DocumentLanguage />
              {children}
            </LocaleProvider>
          </AuthProvider>
        </ApiProvider>
      </UserInfoProvider>
    </ThemeProvider>
  );
}

// The document language follows the chosen UI language, so the browser does
// not take the page for a foreign one and offer to translate it.
function DocumentLanguage() {
  const { locale } = useLocale();
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  return null;
}

function HomeRedirect() {
  const { data: me, isLoading } = useMe();
  if (isLoading || !me) {
    return <FullPageSpinner />;
  }
  return <Redirect to={`/${orgNameToSegment(homeOrgName(me))}`} replace />;
}

// Paths below are relative to `/:org`.
function OrgRoutes() {
  return (
    <Switch>
      <Route path="/" component={CustomerSelector} />
      <Route path="/c/:id" component={CustomerHub} />
      <Route path="/c/:id/events" component={EventsPage} />
      <Route path="/c/:id/zones" component={ZonesPage} />
      <Route path="/c/:id/partitions" component={PartitionsPage} />
      <Route path="/c/:id/users" component={UsersPage} />
      <Route path="/c/:id/notifiables" component={NotifiablesPage} />
      <Route>
        <Redirect to="/" replace />
      </Route>
    </Switch>
  );
}

function AppRoutes() {
  const [sid] = useUserInfo();

  return (
    <Switch>
      <Route path="/login">{sid ? <Redirect to="/" replace /> : <Login />}</Route>
      {!sid && (
        <Route>
          <Redirect to="/login" replace />
        </Route>
      )}
      <Route path="/" component={HomeRedirect} />
      <Route path="/:org" nest>
        <OrgScope>
          <OrgRoutes />
        </OrgScope>
      </Route>
    </Switch>
  );
}

export default function App() {
  return (
    <Providers>
      <Router>
        <AppRoutes />
      </Router>
    </Providers>
  );
}
